import { Link } from 'react-router-dom';
import { Gamepad2, Star, Trophy, Palette, Map, Zap, Heart } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import BlockyCharacter3D from '../components/BlockyCharacter3D';

const ForPupilsPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-sunny-yellow/20 via-cream to-mint-green/10">
      <Navbar />
      
      {/* Hero */}
      <section className="pt-32 pb-20 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center gap-2 bg-white border-2 border-sunny-yellow/30 rounded-full px-4 py-2 mb-8 shadow-md">
              <Gamepad2 className="w-4 h-4 text-sunny-yellow" />
              <span className="font-display text-sm text-sunny-yellow font-bold">FOR PUPILS</span>
            </div>
            
            <h1 className="font-display text-5xl md:text-6xl text-text-dark mb-6" data-testid="pupils-headline">
              YOUR TOWN.<br />
              YOUR <span className="text-mint-green">CHARACTER</span>.<br />
              YOUR <span className="text-sunny-yellow">ADVENTURE</span>!
            </h1>
            
            <p className="font-body text-lg text-text-medium max-w-2xl mx-auto mb-8">
              Welcome to TypeTown! Build your very own town. Make friends with neighbours. 
              Every correct keystroke makes your town grow bigger and happier!
            </p>
            
            <div className="flex justify-center gap-4 items-end mb-10">
              <BlockyCharacter3D size={90} skinTone="#5C3A21" hairColor="#FF69B4" shirtColor="#EC4899" pantsColor="#3B82F6" hairStyle="braids" accessory="glasses" direction="right" />
              <BlockyCharacter3D size={140} skinTone="#3D2314" hairColor="#00CED1" shirtColor="#22C55E" pantsColor="#1F2937" hairStyle="afro" accessory="headband" eyeStyle="happy" />
              <BlockyCharacter3D size={90} skinTone="#F5D0B0" hairColor="#8B5CF6" shirtColor="#F59E0B" pantsColor="#7C3AED" hairStyle="ponytail" accessory="bow" direction="left" />
            </div>
            
            <Link
              to="/login"
              data-testid="pupils-play-btn"
              className="font-display uppercase tracking-wider bg-gradient-to-r from-sunny-yellow to-coral-orange text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg inline-block"
            >
              Play Now →
            </Link>
            <p className="font-body text-sm text-text-medium mt-4">100% free to play &middot; No card required</p>
          </div>
        </div>
      </section>
      
      {/* TypeTown World */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-display text-sm text-mint-green font-bold">TYPETOWN WORLD</span>
            <h2 className="font-display text-4xl text-text-dark mt-2">A Friendly Place to Explore!</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white border-2 border-mint-green/30 rounded-2xl p-6 hover:border-mint-green hover:shadow-lg transition-all" data-testid="grid-feature-1">
              <Map className="w-10 h-10 text-mint-green mb-4" />
              <h3 className="font-display text-xl text-text-dark mb-2">Your Town</h3>
              <p className="font-body text-text-medium">
                Every player has their own town in TypeTown. Type accurately and watch it grow bigger and more beautiful!
              </p>
            </div>
            
            <div className="bg-white border-2 border-coral-orange/30 rounded-2xl p-6 hover:border-coral-orange hover:shadow-lg transition-all" data-testid="grid-feature-2">
              <Heart className="w-10 h-10 text-coral-orange mb-4" />
              <h3 className="font-display text-xl text-text-dark mb-2">Friendly Neighbours</h3>
              <p className="font-body text-text-medium">
                Meet fun characters who live in TypeTown! Each one has stories to share and typing challenges to try!
              </p>
            </div>
            
            <div className="bg-white border-2 border-sunny-yellow/30 rounded-2xl p-6 hover:border-sunny-yellow hover:shadow-lg transition-all" data-testid="grid-feature-3">
              <Trophy className="w-10 h-10 text-sunny-yellow mb-4" />
              <h3 className="font-display text-xl text-text-dark mb-2">Become a Champion!</h3>
              <p className="font-body text-text-medium">
                Start as a Newcomer and work your way up to Champion! Each stage unlocks new areas and buildings!
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Stages Preview */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <span className="font-display text-sm text-lavender font-bold">5 STAGES TO MASTER</span>
            <h2 className="font-display text-4xl text-text-dark mt-2">Level Up Your Typing!</h2>
          </div>
          
          <div className="space-y-4">
            {[
              { stage: 1, icon: '🏕️', name: 'Newcomer', desc: 'Start your adventure! Learn the basics with simple words.', color: '#FF6B6B', unlocks: 'Town Centre' },
              { stage: 2, icon: '🗺️', name: 'Explorer', desc: 'Getting better! Try two or three words at a time.', color: '#FFD93D', unlocks: 'Northern Forest' },
              { stage: 3, icon: '🏗️', name: 'Builder', desc: 'Growing stronger! Type short phrases with punctuation.', color: '#6BCB77', unlocks: 'Eastern Hills' },
              { stage: 4, icon: '⭐', name: 'Master', desc: 'Almost there! Handle full sentences with confidence.', color: '#4ECDC4', unlocks: 'Southern Beach' },
              { stage: 5, icon: '🏆', name: 'Champion', desc: 'You made it! Type paragraphs like a pro!', color: '#A66CFF', unlocks: 'Western Mountains' },
            ].map((s) => (
              <div 
                key={s.stage} 
                className="bg-white border-2 rounded-2xl p-5 flex items-center gap-6 hover:shadow-lg transition-all"
                style={{ borderColor: `${s.color}40` }}
                data-testid={`stage-${s.stage}`}
              >
                <div 
                  className="w-16 h-16 rounded-2xl flex items-center justify-center text-3xl"
                  style={{ backgroundColor: `${s.color}20` }}
                >
                  {s.icon}
                </div>
                <div className="flex-1">
                  <h3 className="font-display text-lg" style={{ color: s.color }}>
                    Stage {s.stage}: {s.name}
                  </h3>
                  <p className="font-body text-text-medium text-sm">{s.desc}</p>
                </div>
                <div className="text-right">
                  <span className="font-display text-xs text-text-medium">UNLOCKS</span>
                  <p className="font-body text-sm font-semibold" style={{ color: s.color }}>{s.unlocks}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      
      {/* Character Creator Preview */}
      <section className="py-16 px-4 bg-white/50">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="font-display text-sm text-lavender font-bold">CHARACTER STUDIO</span>
              <h2 className="font-display text-4xl text-text-dark mt-2 mb-6">Create Your Own Character!</h2>
              <p className="font-body text-lg text-text-medium mb-6">
                Design a character that looks just like you - or completely different! 
                Choose your hair, clothes, and style. Make it yours!
              </p>
              
              <div className="space-y-3">
                {[
                  '🎨 Pick your favourite colours',
                  '💇 Choose from 6 different hairstyles',
                  '👕 Customise your shirt and trousers',
                  '😊 Select your expression',
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-lg">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center">
              <div className="bg-white rounded-3xl p-8 shadow-lg border-2 border-lavender/30">
                <div className="flex gap-6 justify-center mb-6">
                  <BlockyCharacter3D size={80} skinTone="#3D2314" hairColor="#4169E1" shirtColor="#4A90D9" pantsColor="#1F2937" hairStyle="braids" accessory="glasses" />
                  <BlockyCharacter3D size={80} skinTone="#FFE0BD" hairColor="#FF69B4" shirtColor="#EC4899" pantsColor="#FF69B4" hairStyle="pigtails" accessory="catears" />
                  <BlockyCharacter3D size={80} skinTone="#5C3A21" hairColor="#B87333" shirtColor="#EF4444" pantsColor="#78716C" hairStyle="afro" accessory="headband" />
                </div>
                <div className="flex gap-6 justify-center">
                  <BlockyCharacter3D size={80} skinTone="#D4A574" hairColor="#00CED1" shirtColor="#14B8A6" pantsColor="#3B82F6" hairStyle="bob" accessory="bow" />
                  <BlockyCharacter3D size={80} skinTone="#F5D0B0" hairColor="#8B5CF6" shirtColor="#A855F7" pantsColor="#7C3AED" hairStyle="wavy" accessory="necklace" />
                  <BlockyCharacter3D size={80} skinTone="#8D5524" hairColor="#EF4444" shirtColor="#F59E0B" pantsColor="#8B4513" hairStyle="bun" accessory="earrings" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* CTA */}
      <section className="py-20 px-4">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-display text-4xl text-text-dark mb-4">Ready to Start Your Adventure?</h2>
          <p className="font-body text-text-medium mb-8">Join TypeTown today and start building your very own world!</p>
          <Link
            to="/login"
            data-testid="pupils-final-cta"
            className="font-display uppercase tracking-wider bg-gradient-to-r from-mint-green to-sky-blue text-white px-10 py-4 text-xl rounded-2xl hover:scale-105 transition-all shadow-lg inline-block"
          >
            Start Playing! 🎮
          </Link>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default ForPupilsPage;
